import { useState, useEffect, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { Channel, ChannelScope } from 'types/channel';
import { listChannels, ListChannelsResponse } from '../api/marketplaceApi';

const PAGE_SIZE = 20;

export interface ChannelWithSubscription extends Channel {
  is_subscribed?: boolean;
  is_mandatory?: boolean;
  item_count?: number;
  subscriber_count?: number;
}

export interface UseChannelsFilters {
  scope?: 'all' | ChannelScope;
  subscribed?: boolean;
}

export interface UseChannelsResult {
  channels: ChannelWithSubscription[];
  total: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

export function useChannels(filters: UseChannelsFilters = {}): UseChannelsResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [channels, setChannels] = useState<ChannelWithSubscription[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [offset, setOffset] = useState(0);

  const refetch = useCallback(() => {
    setOffset(0);
    setChannels([]);
    setFetchTrigger((prev) => prev + 1);
  }, []);

  const loadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE);
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId) {
      return;
    }

    let cancelled = false;

    const fetchChannels = async () => {
      setLoading(true);
      setError(null);

      try {
        const response: ListChannelsResponse = await listChannels(
          apiClients.schemaClient,
          {
            scope: filters.scope,
            subscribed: filters.subscribed,
            limit: PAGE_SIZE,
            offset,
          }
        );

        if (!cancelled) {
          if (offset === 0) {
            setChannels(response.channels);
          } else {
            setChannels((prev) => [...prev, ...response.channels]);
          }
          setTotal(response.total);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load channels';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchChannels();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, filters.scope, filters.subscribed, fetchTrigger, offset]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
    setChannels([]);
  }, [filters.scope, filters.subscribed]);

  const hasMore = channels.length < total;

  return { channels, total, loading, error, hasMore, loadMore, refetch };
}
